import { useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Icons } from '@/shared/constants/icons';
import { TRANSITION, motionSafe } from '@/shared/constants/motion';
import { useDialogLayer } from '@/shared/hooks/useDialogLayer';

export interface SheetStep {
  key: string;
  label: string;
}

interface SheetProps {
  open: boolean;
  onClose: () => void;
  title: string;
  /** Shown above the title in the mono eyebrow style, e.g. the occasion name. */
  eyebrow?: string;
  /** Back arrow for a stacked flow (A → B → C → D). */
  onBack?: () => void;
  /** Stepper header for a stacked flow; `current` is the index into `steps`. */
  steps?: SheetStep[];
  current?: number;
  /** Primary action row, rendered in the sticky footer. */
  footer?: ReactNode;
  children: ReactNode;
  /** aria-labelledby id is generated from this. */
  id?: string;
  /** Sheet width on desktop. Default 480 (blueprint C11). */
  width?: number;
  /** Keep the sheet open when the backdrop is clicked (mid-generation). */
  closeOnBackdrop?: boolean;
}

/**
 * Right-hand sheet (blueprint C11): 480px from the right on desktops, from
 * the bottom on phones; backdrop dims to 24%; enters with the `panel` timing.
 * Stacked flows keep one sheet and swap its body, so the back arrow and the
 * stepper live here. Portalled to body so no transformed ancestor can trap it.
 */
export function Sheet({ open, onClose, title, eyebrow, onBack, steps, current = 0, footer, children, id = 'sheet', width = 480, closeOnBackdrop = true }: SheetProps) {
  const { t } = useTranslation();
  const reduced = useReducedMotion() ?? false;

  // Escape (top layer only), focus in / trapped / restored, scroll lock.
  const panelRef = useRef<HTMLElement>(null);
  useDialogLayer(open, onClose, panelRef);

  if (typeof document === 'undefined') return null;
  const titleId = `${id}-title`;
  const isPhone = typeof window !== 'undefined' && window.matchMedia?.('(max-width: 767px)').matches;

  return createPortal(
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[600] flex justify-end items-end md:items-stretch" role="presentation">
          <motion.div
            className="absolute inset-0"
            style={{ background: 'var(--scrim)' }}
            initial={reduced ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={reduced ? undefined : { opacity: 0 }}
            transition={motionSafe(TRANSITION.panel, reduced)}
            onClick={closeOnBackdrop ? onClose : undefined}
            aria-hidden="true"
          />
          <motion.aside
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            className="relative bg-[var(--color-surface)] flex flex-col w-full md:w-auto max-h-[92vh] md:max-h-none md:h-full rounded-t-[20px] md:rounded-none border-s border-[var(--line)]"
            style={{ width: isPhone ? '100%' : width, boxShadow: 'var(--shadow-pop)' }}
            initial={reduced ? false : isPhone ? { y: '100%' } : { x: '100%' }}
            animate={{ x: 0, y: 0 }}
            exit={reduced ? undefined : isPhone ? { y: '100%' } : { x: '100%' }}
            transition={motionSafe(TRANSITION.panel, reduced)}
          >
            <header className="flex items-start gap-2 px-5 pt-4 pb-3 border-b border-[var(--line)] shrink-0">
              {onBack ? (
                <button type="button" onClick={onBack} className="btn btn-ghost btn-icon-sm -ms-1.5 shrink-0" aria-label={t('common.back')}>
                  <Icons.chevronLeft size={18} className="rtl:rotate-180" aria-hidden="true" />
                </button>
              ) : null}
              <div className="flex-1 min-w-0">
                {eyebrow && <div className="stat-label truncate" style={{ color: 'var(--ink-3)' }}>{eyebrow}</div>}
                <h2 id={titleId} className="font-display text-title leading-tight m-0 truncate" dir="auto">{title}</h2>
                {steps && steps.length > 1 && (
                  <ol className="flex items-center gap-1.5 mt-2 list-none m-0 p-0" aria-label={t('common.steps')}>
                    {steps.map((s, i) => {
                      const done = i < current;
                      const active = i === current;
                      return (
                        <li key={s.key} className="flex items-center gap-1.5" aria-current={active ? 'step' : undefined}>
                          <span
                            className="h-1.5 rounded-full"
                            style={{ width: active ? 22 : 10, background: done || active ? 'var(--color-accent)' : 'var(--line-2)', transition: 'width var(--motion-micro), background-color var(--motion-micro)' }}
                            aria-hidden="true"
                          />
                          <span className={['text-caption font-medium', active ? 'text-[var(--ink)]' : 'text-[var(--ink-3)]', i === steps.length - 1 ? '' : 'me-1'].join(' ')}>{s.label}</span>
                        </li>
                      );
                    })}
                  </ol>
                )}
              </div>
              <button type="button" onClick={onClose} className="btn btn-ghost btn-icon-sm shrink-0" aria-label={t('common.close')}>
                <Icons.close size={18} aria-hidden="true" />
              </button>
            </header>
            <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4">{children}</div>
            {footer && <footer className="shrink-0 px-5 py-3 border-t border-[var(--line)] bg-[var(--color-surface)] flex items-center gap-2 flex-wrap">{footer}</footer>}
          </motion.aside>
        </div>
      )}
    </AnimatePresence>,
    document.body,
  );
}
